import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { Translation } from '@ps/web-shared-components';
import { addMilliseconds, addSeconds, format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import useTimer from '@swf/core/hooks/useTimer';
import { select } from '@swf/core/store';

import { TXTACL_SessionX_N0, TXTCLI_Limit, TXTCLI_LocalX_0N } from './SessionTimer.messages.json';
import styles from './SessionTimer.scss';

export interface Props {
  showSeconds?: boolean;
  showLimit?: boolean;
  className?: string;
}

const timeZero = new Date(1970, 0, 1, 0, 0, 0);
let initialCount = 0;

const SessionTimer: React.FC<Props> = ({ showSeconds, className, showLimit }) => {
  const {
    userSessionStartTime = 0,
    loginTimeTotalSec = 0,
    loginTimeLimitMin
  } = useSelector(select.account.getLoginLimitInfo);

  const isSiteUS = useSelector(select.cms.isSiteUS);
  const isCasinoBrand = useSelector(select.cms.getBrandId) === BrandTypes.STARS_CASINO;
  const isLocalTimeRequired = isSiteUS && isCasinoBrand;

  const calculateElapsedTime = (): number => {
    const time = Date.now() - userSessionStartTime;
    return time > 0 ? time : 0;
  };

  const initialSessionTime = useMemo(calculateElapsedTime, [userSessionStartTime]);

  const sessionTime = useTimer({
    modifier: calculateElapsedTime,
    initial: initialSessionTime
  });

  const currentTime = useTimer({
    modifier: () => Date.now(),
    initial: Date.now()
  });

  const localTime = formatInTimeZone(currentTime, 'America/New_York', 'HH:mm');

  const timeFormat = showSeconds ? 'HH:mm:ss' : 'HH:mm';
  const time = addMilliseconds(timeZero, sessionTime);
  const limitTime = useTimer({
    modifier: calculateElapsedTime,
    initial: 0
  });

  let sessionLimit = 0;
  if (loginTimeLimitMin === -1) {
    sessionLimit = 0;
    initialCount = limitTime;
  } else {
    const limitCount = initialCount > limitTime ? limitTime : limitTime - initialCount;
    sessionLimit = loginTimeTotalSec + Math.trunc(limitCount / 1000);
  }
  const helperDate = addSeconds(timeZero, sessionLimit);
  const sessionLimitStr = format(helperDate, timeFormat);

  return (
    <>
      <div className={className}>
        {!isLocalTimeRequired ? (
          <>
            <span className={styles.hideMobile}>
              <Translation
                key={sessionTime}
                {...TXTACL_SessionX_N0}
                values={{ 0: format(time, timeFormat) }}
              />
            </span>
            <span className={styles.showMobile}>{format(time, timeFormat)}</span>
          </>
        ) : (
          <>
            <Translation
              key={sessionTime}
              {...TXTACL_SessionX_N0}
              values={{ 0: format(time, timeFormat) }}
            />
            <div>
              <Translation key={localTime} {...TXTCLI_LocalX_0N} values={{ 0: localTime }} />
            </div>
          </>
        )}
      </div>

      {showLimit && (
        <div className={className}>
          <Translation {...TXTCLI_Limit} />
          :&nbsp;{sessionLimitStr}
        </div>
      )}
    </>
  );
};

export default SessionTimer;
